/**
 * Who the credit section is shown to, and what it is honest enough to say.
 *
 * There are exactly TWO credentials the backend will spend credit on
 * (`OrderService.proveCreditOwner`):
 *
 *   - the exchanged claim token, which `/credit/claim` mints for two hours and
 *     `CreditClaimBanner` stores; or
 *   - the signed-in account itself. With no token the server falls back to the
 *     AUTHENTICATED caller's own address and needs no further proof, and
 *     `useOrderCredit` already posts `{}` in that case.
 *
 * The section used to key off the token alone. That made the second credential
 * unreachable: two hours after clicking the link in their email, a signed-in
 * customer's own credit became silently unspendable, with no explanation and no
 * way back except finding the email again.
 *
 * THE LINE, and it is drawn where the server draws it:
 *
 *   - signed in  -> show, with or without a token. The server can prove who
 *     they are, so a request exists that can succeed.
 *   - guest      -> show only while a live token is held. A guest has no
 *     identity for the server to check, so with no token every apply is
 *     refused with CREDIT_PROOF_REQUIRED before it reaches the ledger. An
 *     Apply button there is an invitation to a guaranteed failure.
 *
 * WHAT IT MAY CLAIM. `GET /credit/balance` answers "does this customer have
 * credit?" for exactly the two credentials above, so when it has answered,
 * its answer decides:
 *
 *   - a balance above zero: "you have credit" is a FACT, said plainly.
 *   - a balance of zero: there is nothing to offer, and no control is shown.
 *     This is what the endpoint was built for — before it, every signed-in
 *     customer saw a speculative offer and a zero-credit account found out by
 *     pressing it.
 *
 * When it has NOT answered — still loading, failed, or refused a token — the
 * decision falls back to what it was before the endpoint existed, so a broken
 * balance read can never make the offer worse than it used to be:
 *
 *   - a live token means `/credit/claim` verified a real lot for this address
 *     moments ago: "confirmed" — but only while the read is still LOADING.
 *     Once it has failed or been refused, the token is no evidence any more
 *     (a token refused as invalid is dropped; a spent card's token is now
 *     answered with a balance of zero instead), so the offer drops to
 *     "possible".
 *   - signed in with no token it is a guess, so the offer is phrased as a
 *     condition ("if you have credit on this address...") — a control they can
 *     press, never a balance we do not know.
 *
 * Pure, and tested as such: `.tsx` is excluded from vitest, so the decision
 * lives here rather than in `CreditSection`'s JSX.
 */
import { creditProblemMessage, type CreditProblem } from "./credit-problem";
import { OrderStatus } from "src/enums/order";

/** The statuses the server's `assertCartMutable` lets a cart change in. */
const CART_MUTABLE_STATUSES: readonly OrderStatus[] = [
  OrderStatus.PENDING,
  OrderStatus.PROCESSING,
];

/** How confident the offer is allowed to sound. */
export type CreditOfferTone =
  /** Credit certainly exists: a known balance, or a verified claim token. */
  | "confirmed"
  /** Signed in, nothing verified: the offer must assert nothing. */
  | "possible";

export type CreditControlState = {
  /**
   * The order can still change — it has not been placed or paid. A placed
   * order must never be offered credit: going Back to the delivery step of a
   * paid order used to show "you have credit, use it", and pressing it hit
   * the server's "this order is already placed" refusal.
   */
  orderEditable: boolean;
  /** Credit is already redeemed against this order. */
  hasCreditApplied: boolean;
  /** How much, in minor units. */
  appliedCents: number;
  /**
   * What `GET /credit/balance` said is STILL spendable, in minor units —
   * after anything already on this order — or null/undefined when it has not
   * answered. Null is "unknown", never zero.
   */
  balanceCents?: number | null;
  /**
   * The balance read failed or was refused, as opposed to not having
   * answered yet. A refused read must not be treated like a loading one.
   */
  balanceFailed?: boolean;
  /** A live exchanged claim token is in storage. */
  hasClaimToken: boolean;
  /**
   * That token names typed gift cards (`scope: "lot"`). It proves those
   * cards even while their money is on this order or spent, so a balance of
   * zero must not hide the box: it is the only way to tick them back on
   * after an untick.
   */
  tokenCoversLots?: boolean;
  /** This browser carries a signed-in session. */
  isSignedIn: boolean;
  /** The last attempt was refused and has something to say. */
  hasProblem: boolean;
  /** The customer unticked "use my credit" on this order. */
  optedOut?: boolean;
  /** Credit was already applied automatically once on this order. */
  autoTried?: boolean;
  /**
   * The balance was refused with CREDIT_ACCOUNT_UNVERIFIED: signed in, no
   * token, email never confirmed. Every apply would be refused the same way.
   */
  accountUnverified?: boolean;
  /**
   * Not editable because a card payment has begun and is not finished:
   * pinned, or declined (`creditLockedByPayment`). Unlike a placed order,
   * the customer is still on this page to pay, and deserves to know why the
   * box they used a minute ago no longer moves.
   */
  paymentStarted?: boolean;
  /**
   * "Imam darilni bon" would be offered on an editable order (gift cards can
   * be redeemed). Its disappearance needs the same explanation.
   */
  codeFieldAvailable?: boolean;
};

export type CreditControl = {
  /** Render the section at all. */
  visible: boolean;
  /** Render the "use my credit" checkbox. False: read-only or nothing. */
  interactive: boolean;
  /** The checkbox state: credit is on the order. */
  checked: boolean;
  /** How sure the offer may sound when credit is NOT yet applied. */
  tone: CreditOfferTone | null;
  /** Spendable, shown beside an unticked box. Null when not known. */
  availableCents: number | null;
  /** Left over after this order, shown beside a ticked box. Null when not known. */
  remainingCents: number | null;
  /** Apply it now, without a click. */
  autoApply: boolean;
  /**
   * A sentence about why there is no offer to make (`creditNoticeMessage`):
   * an unconfirmed account (instead of the checkbox), or a card payment
   * already begun (under the read-only amount, if any).
   */
  notice: CreditNotice | null;
};

export type CreditNotice = "account-unverified" | "payment-started";

/**
 * Everything the "Vaše dobroimetje" box on the delivery step decides.
 *
 * It lives only on the delivery step, never in the cart drawer: credit pays
 * the order total INCLUDING shipping, and in the drawer no shipping has been
 * chosen yet — a EUR 1 cart showed "we applied EUR 5.99 of your credit" with
 * nothing on screen to explain the other EUR 4.99.
 *
 * THE LINE, drawn where the server draws it (`CreditLinkService.proveOwner`):
 * a signed-in account, or a live claim token. A guest with neither has no
 * credential the server would accept, so they see nothing.
 *
 * WHEN THE BALANCE IS KNOWN it decides: above zero the box is offered,
 * ticked for them automatically, with the amount beside it; zero hides the
 * box. When it is NOT known — loading, failed, refused — the offer falls back
 * to the pre-balance behaviour (a verified token, still loading: "you have
 * credit"; otherwise: "if you have credit…"), never auto-applied, because applying money
 * nobody has confirmed exists is a guess made on the customer's behalf.
 *
 * AUTO-APPLY happens once per order and never against the customer's wish:
 * unticking records an opt-out, and the box then stays unticked.
 */
export function creditControl(state: CreditControlState): CreditControl {
  const hidden: CreditControl = {
    visible: state.hasProblem,
    interactive: false,
    checked: false,
    tone: null,
    availableCents: null,
    remainingCents: null,
    autoApply: false,
    notice: null,
  };
  const known = typeof state.balanceCents === "number";
  const balance = known ? (state.balanceCents as number) : null;

  // A placed order is read-only: say what was used, offer nothing. One whose
  // card payment has begun is just as frozen — by the server's pin — but the
  // customer is still here to pay it, so it also says why.
  if (!state.orderEditable) {
    const notice = state.paymentStarted ? ("payment-started" as const) : null;
    if (state.hasCreditApplied) {
      return { ...hidden, visible: true, checked: true, remainingCents: balance, notice };
    }
    // Something to explain only for a customer who could have changed
    // credit a moment ago: a typed card or claim link, known credit, or
    // the gift-card field that has just gone.
    const hadSomethingToChange =
      state.hasClaimToken ||
      (known && (balance as number) > 0) ||
      !!state.codeFieldAvailable;
    return notice && hadSomethingToChange
      ? { ...hidden, visible: true, notice }
      : { ...hidden, visible: false };
  }

  if (state.hasCreditApplied) {
    return {
      ...hidden,
      visible: true,
      interactive: true,
      checked: true,
      remainingCents: balance,
    };
  }

  const credentialed = state.hasClaimToken || state.isSignedIn;
  if (!credentialed) return hidden;

  // Signed in to an unconfirmed account, with no token to prove the address
  // otherwise: a checkbox here could only be refused, and an auto-apply would
  // be refused on every render. Say why, and how to fix it. (With a token —
  // a typed gift card, say — the server still honours that token.)
  if (state.accountUnverified && !state.hasClaimToken) {
    return { ...hidden, visible: true, notice: "account-unverified" };
  }

  if (known) {
    if ((balance as number) <= 0) {
      // Typed cards the customer just unticked: the release and the balance
      // read race, and a zero here hid the one control that puts the cards
      // back (bug hunt A, I2). Offered, never auto-applied at zero.
      if (!state.tokenCoversLots || !state.hasClaimToken) return hidden;
      return {
        ...hidden,
        visible: true,
        interactive: true,
        tone: "confirmed",
        availableCents: 0,
      };
    }
    return {
      ...hidden,
      visible: true,
      interactive: true,
      tone: "confirmed",
      availableCents: balance,
      autoApply: !state.optedOut && !state.autoTried && !state.hasProblem,
    };
  }

  return {
    ...hidden,
    visible: true,
    interactive: true,
    tone: state.hasClaimToken && !state.balanceFailed ? "confirmed" : "possible",
  };
}

/**
 * The sentence for a `CreditNotice`, in the house call form.
 *
 * "payment-started" is the server's pin (`pricingOverrideVersion`), which is
 * set once when a card checkout begins and never released — not by Back,
 * not by a decline. The web cannot lift it, so the sentence says what the
 * customer CAN do: pay the amount shown, or write to us.
 */
export function creditNoticeMessage(notice: CreditNotice): {
  key: string;
  fallback: string;
} {
  if (notice === "payment-started") {
    return {
      key: "credit.paymentStarted",
      fallback:
        "Plačilo za to naročilo je že v teku, zato dobroimetja in darilnih bonov zdaj ni mogoče dodati ali odstraniti. Naročilo lahko plačate po prikazanem znesku. Če želite kaj spremeniti, nam pišite in uredili bomo.",
    };
  }
  return creditProblemMessage(notice);
}

/**
 * The translation key and the Slovenian text that renders if a locale file is
 * missing it — the house call form, `t(key, slovenianFallback)`, as
 * `creditProblemMessage` uses.
 */
export function creditOfferMessage(tone: CreditOfferTone): {
  key: string;
  fallback: string;
} {
  return tone === "confirmed"
    ? {
        key: "credit.available",
        fallback: "Na voljo imate dobroimetje. Uporabite ga pri tem naročilu.",
      }
    : {
        key: "credit.maybeAvailable",
        fallback:
          "Če imate na tem e-naslovu dobroimetje, ga lahko uporabite pri tem naročilu.",
      };
}

/**
 * What it means when an apply succeeded but moved no money.
 *
 * `CreditService.redeem` returns 0 rather than refusing when the address has no
 * usable lot, so the endpoint answers 200 and nothing on the screen changes.
 * For a signed-in customer pressing a speculative offer that is the COMMON
 * case, and an unexplained no-op about money is not an acceptable answer.
 *
 * A zero on an empty cart proves nothing: every redemption is capped at the
 * order total, so a total of zero caps it to zero whatever the ledger holds.
 * Saying "no credit found" there would tell a customer who has credit that they
 * do not.
 *
 * Nor does a zero with a KNOWN positive balance mean "no credit": the credit
 * exists but cannot go on this order (typically the order is under the lot's
 * minimum), so that is "not-applicable". Only a zero or unknown balance keeps
 * the "we cannot find credit for this address" sentence.
 *
 * Nothing here compares applied with balance: an apply may legitimately take
 * LESS than the balance (the server leaves the card processor's minimum charge
 * to pay when credit cannot cover the whole order).
 *
 * @param orderTotalCents the PRE-credit total, i.e. `preCreditTotalCents`.
 * @param balanceCents what `GET /credit/balance` said before the apply; null
 *        or undefined when it had not answered.
 */
export function creditApplyProblem(args: {
  appliedCents: number;
  orderTotalCents: number;
  balanceCents?: number | null;
}): CreditProblem | null {
  if (args.appliedCents > 0) return null;
  if (args.orderTotalCents <= 0) return null;
  if (typeof args.balanceCents === "number" && args.balanceCents > 0) {
    return "not-applicable";
  }
  return "unknown";
}

/**
 * Can credit still be put on, or taken off, this order? The SAME two rules the
 * server enforces in `applyCreditToOrder`, so the box never offers a change
 * the server will refuse:
 *
 *  - still a cart: `assertCartMutable` allows ONLY PENDING and PROCESSING.
 *    Not TERMINAL_STATUSES — that list decides whether an order can be PAID
 *    and deliberately leaves FAILED payable (a declined card may retry).
 *    Using it here offered, and auto-applied, credit on an unpinned FAILED
 *    order, which the server refused with a code-less 400;
 *  - not PRICE-PINNED. `pricingOverrideVersion` is stamped the moment a card
 *    checkout is created, and from then on the open Stripe page holds the old
 *    amount; adding credit now could let a customer pay that page in full AND
 *    spend the credit. A customer who went to Stripe and pressed Back is in
 *    exactly this state: status PROCESSING, unpaid, and pinned. Before this
 *    check the box auto-applied there and showed "this order is already
 *    placed", which it was not.
 *
 * Truthiness on purpose, NOT presence: the server tests
 * `if (order.pricingOverrideVersion)` on a string column, so the version "0"
 * IS a pin and an empty string is not. Matching the server exactly is the
 * point.
 */
export function creditEditable(order: unknown): boolean {
  const o = order as { status?: OrderStatus; pricingOverrideVersion?: unknown } | undefined;
  // Not read yet: nothing is known, so nothing may be applied. The balance
  // can answer before the order does, and an auto-apply fired then skipped
  // the pin check above and met the server's refusal (found in the app).
  if (!o || !o.status) return false;
  if (!CART_MUTABLE_STATUSES.includes(o.status)) return false;
  if (o.pricingOverrideVersion) return false;
  return true;
}

/**
 * A card payment has begun on this order and it is not paid: price-pinned
 * while still PENDING or PROCESSING (Back from Stripe), or FAILED (declined,
 * and payable again). Credit is frozen on it — `creditEditable` is false —
 * but, unlike a placed order, the customer is still on the page to pay it.
 */
export function creditLockedByPayment(order: unknown): boolean {
  const o = order as { status?: OrderStatus } | undefined;
  if (!o?.status) return false;
  if (o.status === OrderStatus.FAILED) return true;
  return CART_MUTABLE_STATUSES.includes(o.status) && !creditEditable(order);
}

/**
 * The heading over the credit box. A lot-scoped token (a typed gift-card
 * code) proves one card, and GET /credit/balance then answers for that card
 * alone — calling it "your credit" would pass one card off as the whole
 * account. Signed in, the account is proof too, and the balance is theirs.
 */
export function creditTitle(input: {
  tokenScope: "lot" | "address" | null;
  isSignedIn: boolean;
}): "giftCard" | "credit" {
  return input.tokenScope === "lot" && !input.isSignedIn ? "giftCard" : "credit";
}

/**
 * The line a guest sees where the credit box would be: how to reach credit
 * they may have. Static on purpose: the server must not reveal whether an
 * address holds credit (privacy), so it is shown to every guest who could
 * not spend credit yet, whether or not any exists. Without it a guest who
 * typed the address their credit sits on saw nothing and concluded it was
 * gone (guest cashback walk, case 2).
 *
 * Only for a guest with no credential: a claim token or a signed-in session
 * already lets the box speak for itself, and credit on the order is shown.
 */
export function guestCreditHint(input: {
  orderEditable: boolean;
  isSignedIn: boolean;
  hasClaimToken: boolean;
  hasCreditApplied: boolean;
}): { key: string; fallback: string } | null {
  if (!input.orderEditable) return null;
  if (input.isSignedIn || input.hasClaimToken || input.hasCreditApplied) return null;
  return {
    key: "credit.guestHint",
    fallback:
      "Imate dobroimetje? Odprite povezavo iz e-pošte »Na vaš račun smo dodali …« ali se prijavite.",
  };
}
